import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';

function RecycleBinSection({ siteTimezone, formatDate, formatTime, formatDateTime }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRecycleBin();
  }, []);

  const fetchRecycleBin = async () => {
    try {
      const response = await axios.get('/admin/recycle-bin');
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch recycle bin:', error);
      toast.error('Failed to fetch recycle bin');
    }
  };

  const handleRestore = async (itemType, itemId) => {
    setLoading(true);
    try {
      await axios.post(`/admin/recycle-bin/${itemType}/${itemId}/restore`);
      toast.success('✅ Item restored successfully!');
      fetchRecycleBin();
    } catch (error) {
      console.error('Failed to restore item:', error);
      toast.error('Failed to restore item');
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentDelete = async (itemType, itemId) => {
    if (!window.confirm('Permanently delete this item? This cannot be undone!')) {
      return;
    }
    setLoading(true);
    try {
      await axios.delete(`/admin/recycle-bin/${itemType}/${itemId}`);
      toast.success('✅ Item permanently deleted');
      fetchRecycleBin();
    } catch (error) {
      console.error('Failed to delete item:', error);
      toast.error('Failed to delete item');
    } finally {
      setLoading(false);
    }
  };

  // Group items by type
  const groupedItems = items.reduce((acc, item) => {
    const type = item.type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {});

  const totalItems = items.length;

  const handleEmptyAll = async () => {
    if (!window.confirm('⚠️ Permanently delete ALL items in recycle bin? This cannot be undone!')) {
      return;
    }
    setLoading(true);
    try {
      await axios.delete('/admin/recycle-bin/empty');
      toast.success('✅ Recycle bin emptied successfully');
      fetchRecycleBin();
    } catch (error) {
      console.error('Failed to empty recycle bin:', error);
      toast.error('Failed to empty recycle bin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="text-center flex-1">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Recycle Bin</h2>
          <p className="text-gray-600">Restore or permanently delete items ({totalItems} items)</p>
        </div>
        {totalItems > 0 && (
          <Button 
            variant="destructive"
            onClick={handleEmptyAll}
            disabled={loading}
            className="ml-4"
          >
            🗑️ Empty All
          </Button>
        )}
      </div>

      {totalItems === 0 ? (
        <Card className="p-12 text-center text-gray-500">
          <p>Recycle bin is empty</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Render all items grouped by type */}
          {Object.entries(groupedItems).map(([type, typeItems]) => (
            <Card key={type} className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 capitalize">
                {type}s ({typeItems.length})
              </h3>
              <div className="space-y-2">
                {typeItems.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex-1">
                      <div className="font-medium">
                        {type === 'order' ? (
                          item.order_number ? `Order #${item.order_number}` : `Order ID: ${item.id?.substring(0, 8) || 'N/A'}`
                        ) : (
                          item.name || item.email || item.code || `${type} #${item.id?.substring(0, 8) || 'N/A'}`
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {type === 'order' && item.customer_name && `Customer: ${item.customer_name}`}
                        {type === 'order' && item.total_amount && ` • Total: $${(item.total_amount / 100).toFixed(2)}`}
                        {type === 'user' && item.email && item.email}
                        {item.deleted_at && (
                          <span className="ml-2">
                            Deleted: {formatDate ? formatDate(item.deleted_at, siteTimezone) : new Date(item.deleted_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => handleRestore(type, item.id)} 
                        disabled={loading}
                      >
                        Restore
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handlePermanentDelete(type, item.id)}
                        disabled={loading}
                      >
                        Delete Forever
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default RecycleBinSection;